import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsApi } from '../lib/api';
import {
  LayoutDashboard,
  Globe,
  FileText,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ClipboardList,
  UserCheck,
  ChevronDown,
  Plus,
  Bell,
  Building2,
} from 'lucide-react';

const DashboardSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['applications']);
  const [unreadCount, setUnreadCount] = useState(0);

  const { user, logout, isAdmin, isEmployee } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchNotifCount = async () => {
      try {
        const res = await notificationsApi.getAll();
        if (!isMounted) return;
        setUnreadCount(res.data?.unread_count || 0);
      } catch (error) {
        // Keep the sidebar usable if notifications are temporarily unavailable.
      }
    };

    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000);
    window.addEventListener('notif-updated', fetchNotifCount);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('notif-updated', fetchNotifCount);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const adminMenuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    {
      id: 'applications',
      name: 'Applications',
      icon: FileText,
      children: [
        { name: 'All Applications', href: '/admin/applications' },
        { name: 'Pending Review', href: '/admin/applications?status=submitted' },
        { name: 'Approved', href: '/admin/applications?status=approved' },
        { name: 'Rejected', href: '/admin/applications?status=rejected' },
      ],
    },
    {
      id: 'countries',
      name: 'Countries',
      icon: Globe,
      children: [
        { name: 'All Countries', href: '/admin/countries' },
        { name: 'Add Country', href: '/admin/countries/new' },
      ],
    },
    {
      id: 'visa-types',
      name: 'Visa Types',
      icon: ClipboardList,
      children: [
        { name: 'All Visa Types', href: '/admin/visa-types' },
        { name: 'Add Visa Type', href: '/admin/visa-types/new' },
      ],
    },
    {
      id: 'users',
      name: 'Users',
      icon: Users,
      children: [
        { name: 'All Users', href: '/admin/users' },
        { name: 'Employees', href: '/admin/users?role=employee' },
        { name: 'Add Employee', href: '/admin/employees/new' },
      ],
    },
    { id: 'b2b-partners', name: 'Partners', href: '/admin/partners', icon: Building2 },
    { id: 'admin-contacts', name: 'Contact Us', href: '/admin/contacts', icon: Users },
    { id: 'settings', name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const employeeMenuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/employee', icon: LayoutDashboard },
    {
      id: 'applications',
      name: 'Applications',
      icon: FileText,
      children: [
        { name: 'All Applications', href: '/employee/applications' },
        { name: 'My Assigned', href: '/employee/applications?assigned_only=true' },
        { name: 'Pending Review', href: '/employee/applications?status=submitted' },
      ],
    },
    { id: 'documents', name: 'Document Review', href: '/employee/documents', icon: UserCheck },
  ];

  const applicantMenuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { id: 'applications', name: 'My Applications', href: '/my-applications', icon: FileText },
    { id: 'new-application', name: 'New Application', href: '/apply', icon: Plus },
    { id: 'profile', name: 'Profile', href: '/profile', icon: Users },
  ];

  const b2bMenuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/partner/dashboard', icon: LayoutDashboard },
    ...(user?.approval_status === 'approved'
      ? [
          { id: 'my-applications', name: 'My Applications', href: '/partner/applications', icon: FileText },
          { id: 'new-application', name: 'New Application', href: '/partner/apply', icon: Plus },
        ]
      : []),
    { id: 'profile', name: 'Profile', href: '/profile', icon: Users },
  ];

  const isB2B = user?.role === 'b2b_partner';
  const menuItems = isAdmin
    ? adminMenuItems
    : isEmployee
      ? employeeMenuItems
      : isB2B
        ? b2bMenuItems
        : applicantMenuItems;

  const isActive = (href) => {
    const [path, query] = href.split('?');
    if (location.pathname !== path && !location.pathname.startsWith(`${path}/`)) return false;
    if (!query) return location.pathname === path || location.pathname.startsWith(`${path}/`);
    return new URLSearchParams(location.search).toString() === query;
  };

  const isParentActive = (item) =>
    item.children?.some((child) => isActive(child.href)) || false;

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="lg:hidden fixed top-4 left-4 z-[60] p-2 bg-[#0A1E40] text-white rounded-lg shadow"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        data-testid="sidebar-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`dashboard-sidebar fixed top-0 left-0 z-50 h-screen w-[280px] flex flex-col overflow-hidden ${
          isOpen ? 'open translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="dashboard-sidebar"
      >
        {/* Logo */}
        <div className="shrink-0 p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img
              src="/images/logo1.png"
              alt="EasyMyVisa Logo"
              className="max-w-full object-contain"
              style={{ width: '210px', height: '60px' }}
            />
          </Link>
        </div>

        {/* Scrollable navigation */}
        <nav
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4"
          aria-label="Main navigation"
        >
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const parentActive = isParentActive(item);
              const expanded = expandedMenus.includes(item.id);

              return (
                <li key={item.id}>
                  {item.children ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleMenu(item.id)}
                        className={`sidebar-link w-full justify-between ${
                          parentActive ? 'active' : ''
                        }`}
                        aria-expanded={expanded}
                        data-testid={`sidebar-${item.id}`}
                      >
                        <span className="flex items-center gap-3">
                          <Icon className="w-5 h-5 shrink-0" />
                          <span>{item.name}</span>
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 shrink-0 transition-transform ${
                            expanded ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {expanded && (
                        <ul className="ml-8 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <li key={child.href}>
                              <Link
                                to={child.href}
                                className={`block rounded-lg px-4 py-2 text-sm ${
                                  isActive(child.href)
                                    ? 'text-white bg-white/15'
                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                                }`}
                                onClick={() => setIsOpen(false)}
                              >
                                {child.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                      onClick={() => setIsOpen(false)}
                      data-testid={`sidebar-${item.id}`}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span>{item.name}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Fixed user info and logout */}
        <div className="shrink-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 shrink-0 bg-[#FF5A36] rounded-full flex items-center justify-center">
              <span className="text-white font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-sm truncate">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Dashboard top bar */}
      <div
        className="fixed top-0 right-0 left-0 lg:left-[280px] z-30 bg-[#F5F0E6]/80 backdrop-blur-md border-b border-[#D1CBBF]/50"
        data-testid="dashboard-topbar"
      >
        <div className="flex items-center justify-between h-14 px-4 lg:px-8">
          <div className="lg:hidden w-12" />
          <div className="hidden lg:block" />

          <div className="flex items-center gap-3">
            <Link
              to="/notifications"
              className="relative p-2 rounded-full hover:bg-[#0A1E40]/8 transition-colors"
              data-testid="topbar-notification-bell"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-[#0A1E40]" />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-[#FF5A36] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1"
                  data-testid="topbar-notif-badge"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            <Link
              to="/profile"
              className="flex items-center gap-2 py-1 px-2 rounded-full hover:bg-[#0A1E40]/5 transition-colors"
              data-testid="topbar-user-pill"
            >
              <div className="w-8 h-8 bg-[#0A1E40] rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <span className="hidden sm:inline text-sm font-medium text-[#0A1E40]">
                {user?.name?.split(' ')[0]}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
